/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {useQuery} from '@tanstack/react-query'

import {KTSVG, toAbsoluteUrl} from '../../../../../_metronic/helpers'
import {Loading} from '../../../components/Loadings/loading'
import {LoadingSpan} from '../../../components/Loadings/loadingSpan'
import {SelectGlobal} from '../../../components/SelectGlobal'
import {useToast} from '../../../components/Toats'
import {
  getUnitConsumerAllFilter,
  getUnitConsumerOne,
  updateUnitConsumerAll,
} from '../../../../services/unitConsumerService'
import {UnitConsumerMonitoringFields} from '../UnitConsumerMonitoringFields'

type Props = {
  show: boolean
  handleClose: () => void
  concessionaire: any | null
  unitConsumerId?: string
}

const modalsRoot = document.getElementById('root-modals') || document.body

const DEFAULT_CONCESSIONAIRE_IMAGE =
  'https://res.cloudinary.com/deq3ehxe3/image/upload/v1771533247/1275576_zn46s3.png'

const getConcessionaireImageSrc = (linkImagem?: string | null) => {
  if (linkImagem) {
    return linkImagem.startsWith('http') ? linkImagem : toAbsoluteUrl(linkImagem)
  }

  return toAbsoluteUrl(DEFAULT_CONCESSIONAIRE_IMAGE)
}

const ModalEditUnitConsumerMonitoring = ({show, handleClose, concessionaire, unitConsumerId}: Props) => {
  const {toggleToast} = useToast()
  const [selectedUnitConsumerId, setSelectedUnitConsumerId] = useState('')
  const [emailsRedirect, setEmailsRedirect] = useState<any[]>([])
  const [codeEmail, setCodeEmail] = useState<string | null>(null)
  const [loadingUnitConsumer, setLoadingUnitConsumer] = useState(false)
  const [saving, setSaving] = useState(false)

  const formik = useFormik({
    initialValues: {
      username: null as string | null,
      password: null as string | null,
      provided_email_concessionarie: false,
    },
    validateOnChange: false,
    onSubmit: () => {},
  })

  const {data: unitConsumers = [], isLoading: loadingList} = useQuery<any[]>({
    queryKey: ['unitConsumersByConcessionaire', concessionaire?.name, show],
    queryFn: async () => {
      if (!concessionaire?.name) return []

      return await getUnitConsumerAllFilter(concessionaire.name, undefined)
    },
    enabled: show && !!concessionaire?.name,
    //staleTime: 1000 * 60 * 2,
  })

  const loadUnitConsumer = async (id: string) => {
    if (!id) return

    try {
      setLoadingUnitConsumer(true)
      const myData = await getUnitConsumerOne(id)

      formik.setValues({
        username: myData.username ? myData.username : null,
        password: myData.password ? myData.password : null,
        provided_email_concessionarie: myData.provided_email_concessionarie ?? false,
      })
      setCodeEmail(myData.codeEmail ? myData.codeEmail : null)
      setEmailsRedirect(myData.emailsRedirectUC ? myData.emailsRedirectUC : [])
    } catch (error) {
      toggleToast('Erro ao buscar a unidade consumidora.', 'error')
    } finally {
      setLoadingUnitConsumer(false)
    }
  }

  useEffect(() => {
    if (!show) {
      setSelectedUnitConsumerId('')
      setEmailsRedirect([])
      setCodeEmail(null)
      formik.resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  useEffect(() => {
    if (show && unitConsumerId) {
      setSelectedUnitConsumerId(unitConsumerId)
    }
  }, [show, unitConsumerId])

  useEffect(() => {
    if (selectedUnitConsumerId) {
      loadUnitConsumer(selectedUnitConsumerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnitConsumerId])

  const handleSave = async () => {
    if (!selectedUnitConsumerId) {
      toggleToast('Selecione uma unidade consumidora.', 'error')
      return
    }

    try {
      setSaving(true)
      const myData: any = {
        username: formik.values.username,
        password: formik.values.password,
        provided_email_concessionarie: formik.values.provided_email_concessionarie,
        idConcessionarie: concessionaire?.id,
      }

      Object.keys(myData).forEach((prop) => {
        if (myData[prop] === null || myData[prop] === undefined) {
          delete myData[prop]
        }
      })

      await updateUnitConsumerAll(myData, selectedUnitConsumerId)
      toggleToast('Sucesso ao atualizar a unidade consumidora.', 'success')
      handleClose()
    } catch (error: any) {
      toggleToast(
        error?.response?.data?.message || 'Erro ao atualizar a unidade consumidora.',
        'error'
      )
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <Modal
      id='kt_modal_concessionarie_unit_consumer'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-900px'
      show={show}
      onHide={handleClose}
      backdrop={true}
    >
      <div className='modal-header'>
        <div className='d-flex align-items-center'>
          <div className='symbol symbol-45px me-5'>
            <span
              className='symbol-label'
              style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}
            >
              <img
                src={getConcessionaireImageSrc(concessionaire?.link_imagem)}
                alt='Concessionária'
                className='svg-icon-muted'
                style={{width: '35px', height: '35px', objectFit: 'contain'}}
              />
            </span>
          </div>

          <h3 className='card-title align-items-start flex-column mb-0'>
            <span className='card-label fw-bold fs-3 mb-1'>
              {concessionaire?.name || 'Concessionária'}
            </span>
            {/* {concessionaire?.sub_name && (
              <span className='text-muted fw-semibold fs-7 mt-1'>{concessionaire.sub_name}</span>
            )} */}
          </h3>
        </div>

        <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>

      <div className='modal-body py-lg-8 px-lg-10'>
        <div className='notice d-flex bg-light-info rounded border-info border border-dashed p-6 mb-6'>
          <i className='bi bi-info-circle fs-2tx text-info me-4'></i>
          <div className='d-flex flex-stack flex-grow-1'>
            <div className='fw-bold'>
              <h4 className='text-gray-900 fw-bolder mb-2'>Configuração de monitoramento</h4>
              <div className='fs-6 text-gray-700 fw-semibold'>
                <p className='mb-2'>
                  Nesta tela você configura o monitoramento das unidades consumidoras vinculadas a
                  esta concessionária. Selecione uma unidade abaixo para gerenciar suas informações
                  de acesso.
                </p>
                <p className='mb-2'>
                  As concessionárias podem operar com dois tipos de monitoramento:
                </p>
                <ul className='mb-2 ps-4'>
                  <li className='mb-1'>
                    <strong>E-mail:</strong> utiliza um e-mail exclusivo para receber faturas e
                    realizar o vínculo com o sistema da concessionária.
                  </li>
                  <li>
                    <strong>Credenciais:</strong> utiliza usuário e senha de acesso ao portal da
                    concessionária para extração automatizada das faturas.
                  </li>
                </ul>
                {concessionaire?.type_monitor && (
                  <p className='mb-0'>
                    Esta concessionária utiliza monitoramento por{' '}
                    <strong>
                      {concessionaire.type_monitor === 'EMAIL' ? 'E-MAIL' : 'CREDENCIAIS'}
                    </strong>
                    .
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='mb-6'>
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <label className='form-label fw-bold fs-6 mb-0'>Unidade consumidora</label>
            {!loadingList && (
              <span className='badge badge-light-primary fs-8 fw-semibold'>
                {unitConsumers.length}{' '}
                {unitConsumers.length === 1 ? 'unidade' : 'unidades'}
              </span>
            )}
          </div>
          {loadingList ? (
            <div className='d-flex justify-content-center py-5'>
              <Loading myClass='spinner-border' style={{width: '2rem', height: '2rem'}} />
            </div>
          ) : unitConsumers.length > 0 ? (
            <SelectGlobal
              itens={unitConsumers}
              buttonAll={false}
              textSearch='Buscar unidade consumidora'
              textButtonInit='Selecione a unidade consumidora'
              labelKey='name'
              valueKey='id'
              typeIcon='svg'
              linkIcon='/media/icons/duotune/general/gen001.svg'
              sizeIconSvg='2'
              value={selectedUnitConsumerId}
              className='w-100'
              onChange={(value: string) => setSelectedUnitConsumerId(value || '')}
            />
          ) : (
            <div className='card border border-gray-300 border-dashed rounded p-4 d-flex flex-wrap'>
              <div
                className='bg-light rounded'
                style={{
                  width: '100%',
                  minHeight: '10rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  flexDirection: 'column',
                }}
              >
                <KTSVG
                  path='/media/icons/duotune/general/gen004.svg'
                  className='svg-icon-3x me-1'
                />
                <h3 className='card-title d-flex align-items-center justify-content-center flex-column mb-0'>
                  <span className='text-muted fw-semibold fs-7'>
                    Nenhuma unidade consumidora encontrada!
                  </span>
                  <Link
                    to='/consumidoras'
                    className='text-primary fw-semibold fs-7 text-hover-primary mt-2'
                    onClick={handleClose}
                  >
                    Ir para unidades consumidoras
                  </Link>
                </h3>
              </div>
            </div>
          )}
        </div>

        {loadingUnitConsumer && selectedUnitConsumerId && (
          <div className='d-flex justify-content-center py-10'>
            <Loading myClass='spinner-border' style={{width: '2.5rem', height: '2.5rem'}} />
          </div>
        )}

        {!loadingUnitConsumer && selectedUnitConsumerId && concessionaire && (
          <UnitConsumerMonitoringFields
            concessionarie={concessionaire}
            formik={formik}
            unitConsumerId={selectedUnitConsumerId}
            codeEmail={codeEmail}
            dataEmails={emailsRedirect}
            refresh={() => loadUnitConsumer(selectedUnitConsumerId)}
          />
        )}
      </div>

      <div className='modal-footer'>
        <button type='button' className='btn btn-light' onClick={handleClose}>
          Cancelar
        </button>
        <button
          type='button'
          className='btn btn-primary'
          disabled={!selectedUnitConsumerId || saving || unitConsumers.length === 0}
          onClick={handleSave}
        >
          {!saving && 'Salvar'}
          {saving && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Salvando... <LoadingSpan style={{marginLeft: '0.5rem'}} />
            </span>
          )}
        </button>
      </div>
    </Modal>,
    modalsRoot
  )
}

export {ModalEditUnitConsumerMonitoring}
